import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useDotloopComplianceGroupsQuery } from '@client/graphql/__generated__/document-operations';
import {
  DotloopFolderComplianceStatusInput,
  ExternalIngestionDocumentFragment,
  ExternalIngestionFolderFragment,
  ExternalSubmitValidationReviewsInput,
  ExternalValidationReportDataFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentReviewStatus,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionPlatform,
} from '@client/graphql/__generated__/types';
import {
  ArrowRightIcon,
  CheckIcon,
  FolderIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { ComplianceStatusSelect } from '~/common/components/Dotloop/ComplianceStatusSelect';
import { enumToLabel } from '~/services/document/utils/enums';
import { IngestionDocumentReviewStatusSelect } from './components/IngestionDocumentReviewStatusSelect';
import { WriteToPlatformModal } from './components/WriteToPlatformModal';
import {
  getDocumentReviewStatusColor,
  getDocumentReviewStatusText,
} from './utils/documentReviewStatus.utils';
import { formatPlatformName } from './utils/platform.utils';
import { getValidationResultsForDocument } from './utils/validation.utils';

interface ComplianceSummaryProps {
  documents: ExternalIngestionDocumentFragment[];
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  ingestionFolder: ExternalIngestionFolderFragment;
  validationData: ExternalValidationReportDataFragment | null;
  userAddedDocumentIssues?: IngestionDocumentValidationErrorReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  onSubmitValidationReviews?: (
    input: Omit<ExternalSubmitValidationReviewsInput, 'ingestionFolderId'>
  ) => void | Promise<void>;
  onCreateOrUpdateDocumentReview?: (
    ingestionDocumentId: string,
    status: IngestionDocumentReviewStatus,
    version?: string,
    reviewId?: string
  ) => void | Promise<void>;
}

export const ComplianceSummary: FC<ComplianceSummaryProps> = ({
  documents,
  documentReviews = [],
  submittedDocumentReviews = [],
  ingestionFolder,
  validationData,
  userAddedDocumentIssues = [],
  hiddenDocumentValidationReviews = [],
  onSubmitValidationReviews,
  onCreateOrUpdateDocumentReview,
}) => {
  const [dotloopFolderComplianceStatuses, setDotloopFolderComplianceStatuses] =
    useState<DotloopFolderComplianceStatusInput[]>([]);
  const [isWriteToPlatformModalOpen, setIsWriteToPlatformModalOpen] =
    useState(false);

  const { data: dotloopComplianceGroupsData } = useDotloopComplianceGroupsQuery(
    {
      variables: {
        filters: { profileId: ingestionFolder.dotloopProfileId as string },
      },
      skip: !ingestionFolder.dotloopProfileId,
    }
  );

  const dotloopComplianceStatusesMap = useMemo(() => {
    return dotloopComplianceGroupsData?.dotloopComplianceGroups.reduce(
      (acc, group) => {
        const statuses: Record<string, string> = {};
        group.complianceStatuses?.forEach((status) => {
          statuses[status.id as string] = status.name as string;
        });

        acc[group.id as string] = statuses;

        return acc;
      },
      {} as Record<string, Record<string, string>>
    );
  }, [dotloopComplianceGroupsData]);

  // Create map for submitted reviews lookup
  const submittedReviewsByDocId = useMemo(() => {
    const map = new Map<string, IngestionDocumentReviewFragment>();
    submittedDocumentReviews.forEach((review) => {
      map.set(review.ingestionDocument.id, review);
    });

    return map;
  }, [submittedDocumentReviews]);

  // Group user-added document issues by ingestion document ID
  const userAddedIssuesByIngestionDoc = useMemo(() => {
    const groupedIssues = new Map<
      string,
      IngestionDocumentValidationErrorReviewFragment[]
    >();

    userAddedDocumentIssues.forEach((issue) => {
      if (issue.ingestionDocument?.id) {
        const ingestionDocId = issue.ingestionDocument.id;
        if (!groupedIssues.has(ingestionDocId)) {
          groupedIssues.set(ingestionDocId, []);
        }
        const issues = groupedIssues.get(ingestionDocId);
        if (issues) {
          issues.push(issue);
        }
      }
    });

    return groupedIssues;
  }, [userAddedDocumentIssues]);

  // Create a function to get visible validation stats. This is per ingestion document.
  const getVisibleValidationStats = useCallback(
    (document: ExternalIngestionDocumentFragment) => {
      if (!validationData) {
        return { errors: 0, successes: 0 };
      }

      let totalVisibleErrors = 0;
      let totalSuccesses = 0;

      const { errors, successes } = getValidationResultsForDocument(
        document,
        validationData
      );

      // Filter out hidden validation errors
      const visibleValidationErrors = errors.filter((error) => {
        return !hiddenDocumentValidationReviews.some(
          (review) =>
            review.ruleUuid === error.ruleUuid &&
            review.contractDocumentUuid === error.contractDocumentUuid &&
            review.isHidden
        );
      });

      // Add user-added issues
      const userAddedIssues =
        userAddedIssuesByIngestionDoc.get(document.id) || [];
      const visibleUserAddedIssues = userAddedIssues.filter(
        (issue) => !issue.isHidden
      );

      totalVisibleErrors =
        visibleValidationErrors.length + visibleUserAddedIssues.length;
      totalSuccesses = successes.length;

      return { errors: totalVisibleErrors, successes: totalSuccesses };
    },
    [
      validationData,
      userAddedIssuesByIngestionDoc,
      hiddenDocumentValidationReviews,
    ]
  );

  const hasDocumentReviews = documentReviews && documentReviews.length > 0;

  const documentsWithReviews = useMemo(() => {
    // Sort documents with reviewed documents first, then by validation results
    const sortedDocuments = [...documents].sort((a, b) => {
      // For DOTLOOP platform, first compare by complianceGroupId
      if (ingestionFolder.platform === IngestionPlatform.DOTLOOP) {
        const aGroupId = a.complianceGroupId || '';
        const bGroupId = b.complianceGroupId || '';
        const groupComparison = aGroupId.localeCompare(bGroupId);
        if (groupComparison !== 0) {
          return groupComparison;
        }
      }

      // Check if documents have reviews (unsubmitted OR submitted)
      const aHasReview =
        documentReviews.some((r) => r.ingestionDocument.id === a.id) ||
        submittedReviewsByDocId.has(a.id);
      const bHasReview =
        documentReviews.some((r) => r.ingestionDocument.id === b.id) ||
        submittedReviewsByDocId.has(b.id);

      // Reviewed documents come first
      if (aHasReview && !bHasReview) return -1;
      if (!aHasReview && bHasReview) return 1;

      // Within each group (reviewed vs unreviewed), get validation counts for sorting
      const aCounts = getVisibleValidationStats(a);
      const bCounts = getVisibleValidationStats(b);

      // Sort by errors first (most errors first)
      if (aCounts.errors !== bCounts.errors) {
        return bCounts.errors - aCounts.errors;
      }

      // Then by successes (most successes first)
      if (aCounts.successes !== bCounts.successes) {
        return bCounts.successes - aCounts.successes;
      }

      // Then by updated date descending (most recent first)
      const aUpdated = a.documentUpdatedAt
        ? new Date(a.documentUpdatedAt as string)
        : new Date(a.documentCreatedAt as string);
      const bUpdated = b.documentUpdatedAt
        ? new Date(b.documentUpdatedAt as string)
        : new Date(b.documentCreatedAt as string);

      if (aUpdated.getTime() !== bUpdated.getTime()) {
        return bUpdated.getTime() - aUpdated.getTime();
      }

      // Finally sort by name
      return a.documentName.localeCompare(b.documentName);
    });

    return sortedDocuments
      .filter(
        (document) =>
          // Include documents that need review OR have submitted reviews
          (document.needsReview || submittedReviewsByDocId.has(document.id)) &&
          !document.isArchived
      )
      .map((document) => {
        // Find unsubmitted review first
        const unsubmittedReview = documentReviews.find(
          (r) => r.ingestionDocument.id === document.id
        );

        // Find submitted review as fallback
        const submittedReview = submittedReviewsByDocId.get(document.id);

        // Prioritize unsubmitted over submitted review
        const reviewToDisplay = unsubmittedReview || submittedReview;

        // Calculate validation stats for all contract documents in this ingestion document
        const { errors, successes } = getVisibleValidationStats(document);

        return {
          document,
          review: reviewToDisplay,
          isUnsubmittedReview: !!unsubmittedReview,
          hasSubmittedReview: !!submittedReview,
          validationStats: {
            errors,
            successes,
          },
        };
      })
      .reduce(
        (acc, document) => {
          if (
            (!acc.previousDocumentFolderId ||
              acc.previousDocumentFolderId !== document.document.folderId) &&
            ingestionFolder.platform === IngestionPlatform.DOTLOOP
          ) {
            acc.documents.push({
              ...document,
              showFolderStatusQueue: true,
            });
          } else {
            acc.documents.push(document);
          }

          return {
            previousDocumentFolderId: document.document.folderId,
            documents: acc.documents,
          };
        },
        {
          previousDocumentFolderId: '',
          documents: [] as {
            document: ExternalIngestionDocumentFragment;
            review?: IngestionDocumentReviewFragment;
            showFolderStatusQueue?: boolean;
            isUnsubmittedReview: boolean;
            hasSubmittedReview: boolean;
            validationStats: { errors: number; successes: number };
          }[],
        }
      );
  }, [
    documents,
    documentReviews,
    submittedReviewsByDocId,
    ingestionFolder.platform,
    getVisibleValidationStats,
  ]);

  // Using utility functions for status text and color

  return (
    <Box height="100%" overflow="auto" p={6}>
      <VStack align="stretch" maxW="1000px" mx="auto" spacing={6}>
        <HStack alignItems="center" justify="space-between" spacing={2}>
          <Heading fontSize="24px">Review</Heading>
          <Button
            isDisabled={!hasDocumentReviews}
            size="sm"
            onClick={() => setIsWriteToPlatformModalOpen(true)}
          >
            Update Status in {formatPlatformName(ingestionFolder.platform)}
          </Button>
        </HStack>

        {documentsWithReviews.documents.length === 0 ? (
          <Card p={6}>
            <Text color="compliance.text.muted" textAlign="center">
              No documents require review in this folder.
            </Text>
          </Card>
        ) : (
          <VStack align="stretch" spacing={3}>
            {documentsWithReviews.documents.map(
              ({
                document,
                review,
                isUnsubmittedReview,
                hasSubmittedReview,
                validationStats,
                showFolderStatusQueue,
              }) => (
                <Fragment key={document.id}>
                  {showFolderStatusQueue && (
                    <Card key={document.folderId} p={4} pr={2} py={2}>
                      <HStack justify="space-between">
                        <HStack align="center" gap={2}>
                          <Icon as={FolderIcon} />
                          <Text fontSize="md" fontWeight="medium">
                            {document.folderName}
                          </Text>
                        </HStack>

                        {ingestionFolder.platform ===
                          IngestionPlatform.DOTLOOP &&
                          ingestionFolder.dotloopProfileId &&
                          document.complianceGroupId && (
                            <HStack>
                              <Text
                                color="compliance.text.secondary"
                                fontSize="sm"
                              >
                                {
                                  dotloopComplianceStatusesMap?.[
                                    document.complianceGroupId
                                  ]?.[document.complianceStatusId || '']
                                }
                              </Text>
                              <ArrowRightIcon color="gray" size={16} />
                              <Box width="250px">
                                <ComplianceStatusSelect
                                  complianceGroupId={document.complianceGroupId}
                                  profileId={ingestionFolder.dotloopProfileId}
                                  size="sm"
                                  value={
                                    dotloopFolderComplianceStatuses.find(
                                      (status) =>
                                        status.folderId === document.folderId
                                    )?.complianceStatusId
                                      ? {
                                          value:
                                            dotloopFolderComplianceStatuses.find(
                                              (status) =>
                                                status.folderId ===
                                                document.folderId
                                            )?.complianceStatusId as string,
                                        }
                                      : undefined
                                  }
                                  onChange={(value) => {
                                    setDotloopFolderComplianceStatuses(
                                      (prev) => {
                                        const newStatuses = [...prev].filter(
                                          (status) =>
                                            status.folderId !==
                                            document.folderId
                                        );

                                        if (value?.value) {
                                          newStatuses.push({
                                            folderId: document.folderId || '',
                                            complianceGroupId:
                                              document.complianceGroupId || '',
                                            complianceStatusId: value.value,
                                          });
                                        }

                                        return newStatuses;
                                      }
                                    );
                                  }}
                                />
                              </Box>
                            </HStack>
                          )}
                      </HStack>
                    </Card>
                  )}
                  <Card key={document.id} p={4}>
                    <VStack align="stretch" spacing={3}>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="md" fontWeight="medium">
                          {document.documentName}
                        </Text>

                        <Flex align="center" gap={2}>
                          {/* Case 1: Has unsubmitted review overriding submitted - show transition */}
                          {isUnsubmittedReview && hasSubmittedReview ? (
                            <>
                              <Text color="compliance.text.muted" fontSize="xs">
                                Submitted:
                              </Text>
                              <Text
                                color={getDocumentReviewStatusColor(
                                  submittedReviewsByDocId.get(document.id)
                                )}
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                {getDocumentReviewStatusText(
                                  submittedReviewsByDocId.get(document.id)
                                )}
                              </Text>
                              <ArrowRightIcon color="gray" size={16} />
                              <Text
                                color={getDocumentReviewStatusColor(review)}
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                {getDocumentReviewStatusText(review)}
                              </Text>
                            </>
                          ) : (
                            /* Case 2: Only unsubmitted review OR only submitted review OR needs review */
                            <>
                              {/* For existing submissions with no new draft, just show the status directly */}
                              {!isUnsubmittedReview && hasSubmittedReview ? (
                                <Flex align="center" gap={1}>
                                  <Text
                                    color="compliance.text.muted"
                                    fontSize="xs"
                                    fontWeight="normal"
                                  >
                                    Submitted:
                                  </Text>
                                  <Text
                                    color={getDocumentReviewStatusColor(review)}
                                    fontSize="sm"
                                    fontWeight="medium"
                                  >
                                    {getDocumentReviewStatusText(review)}
                                  </Text>
                                </Flex>
                              ) : (
                                /* For documents needing review or with only draft reviews */
                                <>
                                  <Text
                                    color="compliance.text.secondary"
                                    fontSize="sm"
                                  >
                                    {document.complianceStatus
                                      ? enumToLabel(document.complianceStatus)
                                      : ''}
                                  </Text>
                                  {review && (
                                    <>
                                      <ArrowRightIcon color="gray" size={16} />

                                      <IngestionDocumentReviewStatusSelect
                                        value={{ value: review.status }}
                                        onChange={(value) => {
                                          if (!value?.value) {
                                            return;
                                          }

                                          void onCreateOrUpdateDocumentReview?.(
                                            document.id,
                                            value?.value,
                                            document.version,
                                            review.id
                                          );
                                        }}
                                      />
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Flex>
                      </Flex>

                      <Divider />

                      <HStack spacing={4}>
                        {validationStats.successes > 0 && (
                          <Flex align="center" gap={1}>
                            <Icon
                              as={CheckIcon}
                              color="compliance.status.success"
                              size={14}
                            />
                            <Text
                              color="compliance.status.success"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              {validationStats.successes} Successful{' '}
                              {validationStats.successes === 1
                                ? 'Check'
                                : 'Checks'}
                            </Text>
                          </Flex>
                        )}

                        {validationStats.errors > 0 && (
                          <Flex align="center" gap={1}>
                            <Icon
                              as={TriangleAlertIcon}
                              color="compliance.status.error"
                              size={14}
                            />
                            <Text
                              color="compliance.status.error"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              {validationStats.errors}{' '}
                              {validationStats.errors === 1
                                ? 'Issue'
                                : 'Issues'}
                            </Text>
                          </Flex>
                        )}

                        {validationStats.errors === 0 &&
                          validationStats.successes === 0 && (
                            <Text color="compliance.text.muted" fontSize="sm">
                              No validation data available
                            </Text>
                          )}
                      </HStack>
                    </VStack>
                  </Card>
                </Fragment>
              )
            )}
          </VStack>
        )}

        <WriteToPlatformModal
          documentReviews={documentReviews}
          documents={documents}
          dotloopFolderComplianceStatuses={dotloopFolderComplianceStatuses}
          hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
          ingestionFolder={ingestionFolder}
          isOpen={isWriteToPlatformModalOpen}
          userAddedDocumentIssues={userAddedDocumentIssues}
          validationData={validationData}
          onClose={() => setIsWriteToPlatformModalOpen(false)}
          onSubmitValidationReviews={onSubmitValidationReviews}
        />
      </VStack>
    </Box>
  );
};
